import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@data/notification';
import { environmentAtom } from '@data/fms/environment/states';
import type { EditCategories } from '@data/fms/vehicle/types';
import { useAtomCallback } from 'jotai/utils';

export type PostEditableCalibrationParameterBody = {
  calibration_parameter_version_id?: string;
  edit_categories: Omit<EditCategories, 'display_name'>[];
};

/**
 * 編集パラメータでの候補値生成API
 */
export const useCreateNewCalibrationParameterVersionWithEditedParametersAPI =
  (): {
    state: APIState;
    postCreateNewCalibrationParameterVersionWithEditedParameters: (
      vehicleId: string,
      body: PostEditableCalibrationParameterBody,
    ) => Promise<boolean>;
  } => {
    const [state, setState] = useState<APIState>('none');
    const { notifyError, notifySuccess } = useNotification();
    const fmsAPI = useFMSAPI();
    const getHasScope = useHasScope();
    const getErrorMessage = useErrorMessage();
    const { t } = useTranslation();

    const postCreateNewCalibrationParameterVersionWithEditedParametersAPI =
      useCallback(
        async (
          projectId: string,
          environmentId: string,
          vehicleId: string,
          body: PostEditableCalibrationParameterBody,
        ): Promise<Result<boolean, AxiosResponse>> => {
          try {
            await fmsAPI.post(
              `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/editable_calibration_parameter_configs`,
              body,
            );
            return new Success(true);
          } catch (error) {
            return new Failure((error as AxiosError).response as AxiosResponse);
          }
        },
        [fmsAPI],
      );

    const postCreateNewCalibrationParameterVersionWithEditedParameters =
      useAtomCallback(
        useCallback(
          async (
            get,
            _,
            vehicleId: string,
            body: PostEditableCalibrationParameterBody,
          ) => {
            const project = get(projectAtom);
            const environment = get(environmentAtom);
            if (!project || !environment) {
              notifyError({
                message: t(
                  'api.fms.post_editable_calibration_parameter_configs',
                  {
                    status: 'failed',
                  },
                ),
              });
              setState('hasError');
              return false;
            }
            setState('loading');
            if (!getHasScope(SCOPES.CreateNewCalibrationParameterWithEdited)) {
              setState('hasError');
              return false;
            }
            const res =
              await postCreateNewCalibrationParameterVersionWithEditedParametersAPI(
                project!.id,
                environment!.environment_id,
                vehicleId,
                body,
              );
            if (!res.value || res.isFailure()) {
              notifyError({
                message: `${t(
                  'api.fms.post_editable_calibration_parameter_configs',
                  {
                    status: 'failed',
                  },
                )}: ${getErrorMessage(res)}`,
              });
              setState('hasError');
              return false;
            }
            notifySuccess({
              message: t(
                'api.fms.post_editable_calibration_parameter_configs',
                {
                  status: 'successfully',
                },
              ),
            });
            setState('hasValue');
            return res.value;
          },
          [
            notifyError,
            notifySuccess,
            postCreateNewCalibrationParameterVersionWithEditedParametersAPI,
            getHasScope,
            t,
            getErrorMessage,
          ],
        ),
      );

    return {
      state,
      postCreateNewCalibrationParameterVersionWithEditedParameters,
    };
  };
